import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import BaseBreadcrumbs from "../../components/BaseBreadcrumbs";
import Pagination from "../../components/Pagination"; // expects { meta, onChange, pageSizeOptions }

/**
 * UserManagementPage (refactored)
 * - Server-side pagination with normalizeMeta (tolerates DRF-style {count, results})
 * - Search + status filter (username, is_active)
 * - Modals: Create, Edit, Delete confirm
 * - Reused UI primitives (Button/Input/Modal/etc.) consistent with other admin pages
 */

// ---------------- UI primitives ----------------
const cx = (...classes) => classes.filter(Boolean).join(" ");

const Button = ({ as: Tag = "button", className = "", children, ...rest }) => (
  <Tag
    className={cx(
      "inline-flex items-center justify-center gap-2 h-9 px-3 rounded-lg text-sm font-medium",
      "border bg-slate-800/80 border-slate-700 text-slate-200 hover:bg-slate-700/80",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60",
      "disabled:opacity-50 disabled:cursor-not-allowed",
      className
    )}
    {...rest}
  >
    {children}
  </Tag>
);

const PrimaryButton = (props) => (
  <Button {...props} className={cx("border-indigo-700 bg-indigo-600 text-white hover:bg-indigo-500", props.className)} />
);

const DangerButton = (props) => (
  <Button {...props} className={cx("border-red-800 bg-red-900/35 text-red-100 hover:bg-red-900/55", props.className)} />
);

const Input = (props) => (
  <input
    {...props}
    className={cx(
      "h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200",
      "placeholder:text-slate-500",
      "focus:outline-none focus:ring-2 focus:ring-indigo-500/60",
      props.className
    )}
  />
);

const Select = ({ className = "", children, ...rest }) => (
  <select
    {...rest}
    className={cx(
      "h-10 px-3 rounded-lg bg-slate-800 border border-slate-700 text-slate-200",
      "focus:outline-none focus:ring-2 focus:ring-indigo-500/60",
      className
    )}
  >
    {children}
  </select>
);

const Field = ({ label, children, help }) => (
  <label className="grid gap-1">
    <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
    {children}
    {help ? <span className="text-[11px] text-slate-500">{help}</span> : null}
  </label>
);

const Modal = ({ open, title, onClose, children, footer }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-[min(900px,92vw)] rounded-2xl border border-slate-700 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-slate-800">
          <h3 className="text-lg font-semibold text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-slate-200 hover:bg-slate-800"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-4 md:p-6 space-y-4">{children}</div>
        {footer ? (
          <div className="px-4 md:px-6 py-3 border-t border-slate-800 flex items-center justify-end gap-2">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

// ---------------- helpers ----------------
const toBool = (v) => (typeof v === "string" ? v.toLowerCase() === "true" : Boolean(v));

const normalizeMeta = (rawMetaOrDRF, params, itemsLen) => {
  // Accept both {count, results} (DRF) and {meta: {...}}
  const drfCount = typeof rawMetaOrDRF?.count === "number" ? rawMetaOrDRF.count : undefined;
  const m = rawMetaOrDRF?.meta || {};

  const page = Number(m.page ?? params.page ?? 1);
  const page_size = Number(m.page_size ?? params.page_size ?? params.limit ?? 10);
  const skip = Number(m.skip ?? (page - 1) * page_size);
  const limit = Number(m.limit ?? page_size);

  const total = drfCount !== undefined
    ? drfCount
    : m.total !== undefined
    ? Number(m.total)
    : m.total_pages !== undefined
    ? Number(m.total_pages) * limit
    : undefined;

  let has_next = m.has_next;
  let has_prev = m.has_prev;

  if (total !== undefined) {
    const total_pages = Math.max(1, Math.ceil(total / limit));
    has_prev = page > 1;
    has_next = page < total_pages;
    return { page, page_size, skip, limit, total, total_pages, has_next, has_prev };
  }
  has_prev = has_prev ?? page > 1;
  has_next = has_next ?? Number(itemsLen ?? 0) === limit;
  return { page, page_size, skip, limit, total: undefined, total_pages: undefined, has_next, has_prev };
};

const pickKnownCreate = (obj) => ({
  username: obj?.username ?? "",
  email: obj?.email ?? "",
  password: obj?.password ?? "",
  role: obj?.role ?? "user",
  is_active: Boolean(obj?.is_active ?? true),
});

const pickKnownEdit = (obj) => ({
  username: obj?.username ?? "",
  email: obj?.email ?? "",
  role: obj?.role ?? obj?.role_name ?? "user",
  is_active: toBool(obj?.is_active ?? obj?.status ?? true),
  credit: typeof obj?.credit === "number" ? obj.credit : Number(obj?.credit ?? 0),
});

export default function UserManagementPage() {
  const API_BASE = "/admin/users"; // GET /?username=&is_active=&page=&page_size=

  // list state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [username, setUsername] = useState("");
  const [status, setStatus] = useState(""); // "" | "true" | "false"
  const [meta, setMeta] = useState({ page: 1, page_size: 10, skip: 0, limit: 10 });

  // modals
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState(pickKnownCreate({ is_active: true }));

  const [editing, setEditing] = useState(null); // row
  const [editForm, setEditForm] = useState(pickKnownEdit({}));

  const [confirmDel, setConfirmDel] = useState(null); // row

  // columns (explicit)
  const columnKeys = useMemo(() => [
    "username",
    "email",
    "is_active",
    "credit",
    "date_joined",
    "last_login"
  ], []);

  const fetchList = useCallback(async ({ page, page_size }) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        page_size,
        username: username || undefined,
        is_active: status || undefined,
      };
      const { data } = await axiosInstance.get(`${API_BASE}/`, { params });
      // DRF convention: data = { count, results }
      const items = Array.isArray(data?.results) ? data.results : Array.isArray(data?.items) ? data.items : [];
      setUsers(items);
      setMeta(normalizeMeta({ count: data?.count, meta: data?.meta }, params, items.length));
    } catch (e) {
      console.error(e);
      setUsers([]);
      setError("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  }, [API_BASE, username, status]);

  // initial + deps
  useEffect(() => {
    fetchList({ page: 1, page_size: meta.page_size || meta.limit || 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username, status]);

  const getCurrentPage = () => meta?.page || (meta?.skip != null && meta?.limit ? Math.floor(meta.skip / meta.limit) + 1 : 1);

  const reloadCurrentPageSafe = useCallback(async () => {
    const curr = getCurrentPage();
    const size = meta.limit || meta.page_size || 10;
    await fetchList({ page: curr, page_size: size });
    if (!loading && users.length === 0 && (meta?.total !== undefined ? curr > 1 : true)) {
      await fetchList({ page: Math.max(1, curr - 1), page_size: size });
    }
  }, [fetchList, meta, users.length, loading]);

  // CRUD
  const onCreate = async () => {
    const payload = pickKnownCreate(createForm);
    if (!payload.username.trim() || !payload.email.trim() || !payload.password.trim()) {
      setError("Username, Email, Password are required.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axiosInstance.post(`${API_BASE}/`, payload);
      setCreating(false);
      setCreateForm(pickKnownCreate({ is_active: true }));
      await fetchList({ page: 1, page_size: meta.limit || 10 });
    } catch (e) {
      console.error(e);
      setError(String(e?.response?.data?.detail || "Failed to create user"));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (row) => {
    setEditing(row);
    setEditForm(pickKnownEdit(row));
  };

  const onUpdate = async () => {
    if (!editing) return;
    const id = editing.user_id ?? editing.id; // API may use user_id
    if (!id) return setError("Missing user id");
    const payload = pickKnownEdit(editForm);
    setLoading(true);
    setError("");
    try {
      await axiosInstance.put(`${API_BASE}/${id}/`, payload);
      setEditing(null);
      await reloadCurrentPageSafe();
    } catch (e) {
      console.error(e);
      setError(String(e?.response?.data?.detail || "Failed to update user"));
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!confirmDel) return;
    const id = confirmDel.user_id ?? confirmDel.id;
    setLoading(true);
    setError("");
    try {
      await axiosInstance.delete(`${API_BASE}/${id}/`);
      setConfirmDel(null);
      await reloadCurrentPageSafe();
    } catch (e) {
      console.error(e);
      setError(String(e?.response?.data?.detail || "Failed to delete user"));
    } finally {
      setLoading(false);
    }
  };

  // render helpers
  const fmtMoney = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return "$0.00";
    return `$${n.toFixed(2)}`;
  };

  return (
    <div className="bg-gradient-to-br from-black via-gray-900 to-gray-800 text-gray-100 min-h-screen">
      <div className="max-w-full mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
        {/* Breadcrumbs + actions */}
        <div className="flex items-center justify-between">
          <BaseBreadcrumbs />
          <div className="flex gap-2">
            <Button onClick={() => setCreating(true)}>+ Add User</Button>
            <Button className="bg-slate-700/80 hover:bg-slate-700">Export List</Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Search by username/email…"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") fetchList({ page: 1, page_size: meta.limit || meta.page_size || 10 });
            }}
            className="w-72"
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Status</span>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Suspended</option>
            </Select>
          </div>
          <Button onClick={() => fetchList({ page: 1, page_size: meta.limit || meta.page_size || 10 })} disabled={loading}>
            {loading ? "Loading…" : "Search"}
          </Button>
          <Button onClick={() => { setUsername(""); setStatus(""); }}>Clear</Button>
        </div>

        {/* Table */}
        <section>
          <div className="overflow-auto rounded-xl border border-slate-800/80">
            <table className="min-w-full table-auto text-sm">
              <colgroup>
                {columnKeys.map((k) => (<col key={k} />))}
                <col className="w-44" />
              </colgroup>
              <thead className="bg-slate-900/80 sticky top-0 z-10 border-b border-slate-800/80">
                <tr>
                  {columnKeys.map((k) => (
                    <th key={k} className="text-left uppercase tracking-wide text-[11px] text-slate-100 px-3 py-2">{k.replaceAll("_", " ")}</th>
                  ))}
                  <th className="w-44 shrink-0 text-left uppercase tracking-wide text-[11px] text-slate-100 px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/80">
                {loading && users.length === 0 ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {columnKeys.map((k) => (
                        <td key={k} className="px-3 py-3"><div className="h-3 rounded bg-slate-700/50" /></td>
                      ))}
                      <td className="px-3 py-3 text-right w-44 shrink-0"><div className="h-8 w-40 rounded bg-slate-700/50 ml-auto" /></td>
                    </tr>
                  ))
                ) : users.length > 0 ? (
                  users.map((u) => (
                    <tr key={u.user_id ?? u.id ?? u.username} className="odd:bg-slate-900/20 hover:bg-slate-800/30 transition-colors">
                      {columnKeys.map((k) => (
                        <td key={k} className={cx("px-3 py-3 align-middle", k === "username" && "font-medium text-slate-100")}>
                          {k === "is_active"
                            ? (toBool(u.status ?? u.is_active) ? "Active" : "Suspended")
                            : k === "credit"
                            ? fmtMoney(u.credit)
                            : k === "created_at"
                            ? (u.created_at ? new Date(u.created_at).toLocaleDateString() : "—")
                            : (u[k] ?? "—")}
                        </td>
                      ))}
                      <td className="px-3 py-2 align-middle text-left w-44 shrink-0 whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <Link to={`/admin/users/${u.user_id ?? u.id}`} className="px-2 h-8 grid place-items-center rounded-lg bg-slate-700/70 hover:bg-slate-700 text-xs">View</Link>
                          <Button onClick={() => startEdit(u)} className="h-8 px-2">Edit</Button>
                          <DangerButton onClick={() => setConfirmDel(u)} className="h-8 px-2">Delete</DangerButton>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columnKeys.length + 1} className="py-12 text-center text-slate-400">No users found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {users.length > 0 && (
            <div className="mt-4">
              <Pagination
                meta={meta}
                onChange={({ page, page_size }) => fetchList({ page, page_size })}
                pageSizeOptions={[10, 25, 40, 60, 100]}
              />
            </div>
          )}
        </section>

        {/* Error banner */}
        {error && <div className="rounded-xl border border-red-800/50 bg-red-900/35 text-red-100 px-4 py-3">{error}</div>}

        {/* Create Modal */}
        <Modal
          open={creating}
          title="Create User"
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button onClick={() => setCreating(false)}>Cancel</Button>
              <PrimaryButton onClick={onCreate} disabled={loading}>Create</PrimaryButton>
            </>
          }
        >
          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Username">
                <Input value={createForm.username} onChange={(e) => setCreateForm((s) => ({ ...s, username: e.target.value }))} />
              </Field>
              <Field label="Email">
                <Input type="email" value={createForm.email} onChange={(e) => setCreateForm((s) => ({ ...s, email: e.target.value }))} />
              </Field>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Password">
                <Input type="password" value={createForm.password} onChange={(e) => setCreateForm((s) => ({ ...s, password: e.target.value }))} />
              </Field>
              <Field label="Role">
                <Select value={createForm.role} onChange={(e) => setCreateForm((s) => ({ ...s, role: e.target.value }))}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </Field>
            </div>
            <div className="flex items-center gap-2 h-10">
              <input
                id="create_is_active"
                type="checkbox"
                checked={!!createForm.is_active}
                onChange={(e) => setCreateForm((s) => ({ ...s, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500"
              />
              <label htmlFor="create_is_active" className="text-slate-300 text-sm">Active</label>
            </div>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          open={!!editing}
          title={`Edit: ${editing?.username ?? editing?.email ?? "User"}`}
          onClose={() => setEditing(null)}
          footer={
            <>
              <Button onClick={() => setEditing(null)}>Cancel</Button>
              <PrimaryButton onClick={onUpdate} disabled={loading}>Save</PrimaryButton>
            </>
          }
        >
          <div className="grid gap-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Username">
                <Input value={editForm.username} onChange={(e) => setEditForm((s) => ({ ...s, username: e.target.value }))} />
              </Field>
              <Field label="Email">
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm((s) => ({ ...s, email: e.target.value }))} />
              </Field>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Role">
                <Select value={editForm.role} onChange={(e) => setEditForm((s) => ({ ...s, role: e.target.value }))}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </Field>
              <Field label="Credit">
                <Input type="number" step="0.01" value={editForm.credit} onChange={(e) => setEditForm((s) => ({ ...s, credit: Number(e.target.value) }))} />
              </Field>
            </div>
            <div className="flex items-center gap-2 h-10">
              <input
                id="edit_is_active"
                type="checkbox"
                checked={!!editForm.is_active}
                onChange={(e) => setEditForm((s) => ({ ...s, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-slate-600 bg-slate-800 text-indigo-500"
              />
              <label htmlFor="edit_is_active" className="text-slate-300 text-sm">Active</label>
            </div>
          </div>
        </Modal>

        {/* Delete confirm */}
        <Modal
          open={!!confirmDel}
          title="Delete user"
          onClose={() => setConfirmDel(null)}
          footer={
            <>
              <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
              <DangerButton onClick={onDelete} disabled={loading}>Delete</DangerButton>
            </>
          }
        >
          <p className="text-slate-300">Are you sure you want to delete <span className="font-semibold">{confirmDel?.username ?? confirmDel?.email ?? "this user"}</span>?</p>
        </Modal>
      </div>
    </div>
  );
}
